/**
 * Snapshot e recálculo de custos para histórico de alterações de rotas
 * (Original Merus × Editada/Manual).
 */

export const TIPO_EVENTO = {
    ORIGINAL: 'ORIGINAL',
    PUBLICADA: 'PUBLICADA',
    EDITADA_CRUD: 'EDITADA_CRUD',
    CALCULADA: 'CALCULADA',
    INFORMADA: 'INFORMADA',
    /** Traçado que a rota tinha antes da primeira edição no CRUD, quando ela não tinha baseline. */
    TRACADO_ANTERIOR: 'TRACADO_ANTERIOR',
};

/** Eventos que abrem um grupo e servem de base para comparar as alterações seguintes. */
export const TIPOS_EVENTO_BASELINE = [
    TIPO_EVENTO.ORIGINAL,
    TIPO_EVENTO.CALCULADA,
    TIPO_EVENTO.TRACADO_ANTERIOR,
];

export function isTipoEventoBaseline(tipo) {
    return TIPOS_EVENTO_BASELINE.includes(tipo);
}

export const ORIGEM = {
    ROTEIRIZACAO: 'ROTEIRIZACAO',
    CRUD_LINHAS: 'CRUD_LINHAS',
    API_LINHAS: 'API_LINHAS',
};

/** Constância indicada nos textos de custo. */
export const CONSTANCIA_CUSTO = {
    VIAGEM: 'VIAGEM',
    DIARIO: 'DIARIO',
    MENSAL: 'MENSAL',
    CICLO_CONTRATO: 'CICLO_CONTRATO',
};

export function normalizeConstanciaCusto(value) {
    const raw = value != null ? String(value).trim().toUpperCase() : '';
    if (raw === CONSTANCIA_CUSTO.DIARIO || raw === 'DIÁRIO') return CONSTANCIA_CUSTO.DIARIO;
    if (raw === CONSTANCIA_CUSTO.MENSAL) return CONSTANCIA_CUSTO.MENSAL;
    if (raw === CONSTANCIA_CUSTO.CICLO_CONTRATO || raw === 'CICLO') return CONSTANCIA_CUSTO.CICLO_CONTRATO;
    return CONSTANCIA_CUSTO.VIAGEM;
}

function cloneJson(value) {
    if (value == null) return value;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (e) {
        return value;
    }
}

function geojsonToString(geojson) {
    if (geojson == null) return null;
    if (typeof geojson === 'string') return geojson;
    try {
        return JSON.stringify(geojson);
    } catch (e) {
        return null;
    }
}

export function generateIdGrupo() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function metersToKm(meters) {
    const m = Number(meters);
    if (!Number.isFinite(m) || m < 0) return 0;
    return Number((m / 1000).toFixed(3));
}

/**
 * Fórmula alinhada a summarizeFleetCostFromRoutes (por rota, cobrando fixo+labor).
 */
export function computeRouteCost(km, params) {
    const p = params || {};
    const fixed = Number(p.fixedCost) || 0;
    const labor = Number(p.laborCost) || 0;
    const costPerKm = Number(p.costPerKm) || 0;
    const k = Number(km) || 0;
    const variable = costPerKm * k;
    return {
        custo_fixo: Number(fixed.toFixed(2)),
        custo_mao_obra: Number(labor.toFixed(2)),
        custo_variavel: Number(variable.toFixed(2)),
        custo_total: Number((fixed + labor + variable).toFixed(2)),
    };
}

export function buildDiffJson(original, current) {
    const oKm = original && original.km != null ? Number(original.km) : null;
    const cKm = current && current.km != null ? Number(current.km) : null;
    const oCusto = original && original.custo_total != null ? Number(original.custo_total) : null;
    const cCusto = current && current.custo_total != null ? Number(current.custo_total) : null;
    const campos = [];
    if (oKm != null && cKm != null && Math.abs(oKm - cKm) > 0.0005) campos.push('km');
    if (oCusto != null && cCusto != null && Math.abs(oCusto - cCusto) > 0.005) campos.push('custo_total');
    if (
        original && current
        && original.duracao_seg != null && current.duracao_seg != null
        && Number(original.duracao_seg) !== Number(current.duracao_seg)
    ) {
        campos.push('duracao_seg');
    }
    return {
        km_delta: oKm != null && cKm != null ? Number((cKm - oKm).toFixed(3)) : null,
        custo_delta: oCusto != null && cCusto != null ? Number((cCusto - oCusto).toFixed(2)) : null,
        campos_alterados: campos,
    };
}

function resolveVehicleCostParams(route, fleetVehicleSpecById, fleetCostSummary) {
    const vid = route && route.vehicleId != null ? route.vehicleId : null;
    const spec = vid != null && fleetVehicleSpecById ? fleetVehicleSpecById[vid] : null;
    let fixedCost = spec && spec.fixedCost != null ? Number(spec.fixedCost) : 0;
    let costPerKm = spec && spec.costPerKm != null ? Number(spec.costPerKm) : 0;
    let laborCost = 0;
    if (spec) {
        if (spec.laborCost != null) laborCost = Number(spec.laborCost);
        else if (spec.valor_labor != null) laborCost = Number(spec.valor_labor);
    }

    const occupancy = fleetCostSummary
        && Array.isArray(fleetCostSummary.perRouteOccupancy)
        ? fleetCostSummary.perRouteOccupancy
        : [];
    const match = occupancy.find((oc) => {
        if (!oc) return false;
        if (vid != null && String(oc.vehicleId) === String(vid)) {
            if (route._apiRouteIndex != null && oc.routeNumber != null) {
                return Number(oc.routeNumber) === Number(route.routeNumber);
            }
            return true;
        }
        return false;
    });
    if (match) {
        if (match.fixedCost != null) fixedCost = Number(match.fixedCost);
        if (match.variableCost != null && Number(match.km) > 0) {
            costPerKm = Number(match.variableCost) / Number(match.km);
        }
        if (match.laborCost != null) laborCost = Number(match.laborCost);
        if (match.routeCost != null && Number.isFinite(Number(match.routeCost))) {
            // prefer explicit per-route breakdown when present
        }
    }

    return {
        vehicleId: vid,
        fixedCost: Number.isFinite(fixedCost) ? fixedCost : 0,
        costPerKm: Number.isFinite(costPerKm) ? costPerKm : 0,
        laborCost: Number.isFinite(laborCost) ? laborCost : 0,
        capacity: spec && spec.capacity != null ? Number(spec.capacity) : null,
        type: spec && spec.type != null ? spec.type : null,
    };
}

/**
 * Captura baseline Merus por rota operacional (após send + refreshFleetCostSummary).
 * @returns {Map<string|number, object>} keyed by _rotaId
 */
export function captureMerusBaselineFromVm(vm) {
    const map = new Map();
    if (!vm || !Array.isArray(vm.routes)) return map;
    const dest = vm.destination || {};
    const specById = vm.fleetVehicleSpecById || {};
    const summary = vm.fleetCostSummary || null;

    (vm.routes || []).forEach((route) => {
        if (!route || route._displacementLeg) return;
        const key = route._rotaId != null ? route._rotaId : route._apiRouteIndex;
        if (key == null) return;
        const tableRow = (vm.routingTable || []).find(
            (t) => t && t._apiRouteIndex === route._apiRouteIndex,
        );
        const km = metersToKm(route.distancia);
        const params = resolveVehicleCostParams(
            { ...route, routeNumber: tableRow && tableRow.routeNumber },
            specById,
            summary,
        );
        const costs = computeRouteCost(km, params);
        map.set(key, {
            _rotaId: route._rotaId,
            _apiRouteIndex: route._apiRouteIndex,
            vehicleId: route.vehicleId,
            km,
            duracao_seg: Number(route.duracao) || 0,
            geojson: geojsonToString(route.geojson),
            shape: route.shape != null ? String(route.shape) : null,
            parametros_custo_json: params,
            ...costs,
            id_cerca_destino: dest.id_cerca != null ? dest.id_cerca : null,
            nome_destino: dest.nome_cerca || dest.cod_cerca || null,
            id_empresa: vm.routingCompanyId != null ? Number(vm.routingCompanyId) : null,
            id_cliente: vm.routingClientId != null ? Number(vm.routingClientId) : null,
            id_modo_roteirizacao: vm.routingModeId != null ? Number(vm.routingModeId) : null,
            constancia_custo: normalizeConstanciaCusto(vm.costConstancy),
        });
    });
    return map;
}

export function buildOriginalPayload({
    idGrupo,
    baseline,
    editedRoute,
    idUsuario,
    dtEvento,
    idModoRoteirizacao,
    constanciaCusto,
}) {
    return {
        id_grupo: idGrupo,
        id_versao: 1,
        tipo_evento: TIPO_EVENTO.ORIGINAL,
        origem: ORIGEM.ROTEIRIZACAO,
        id_usuario: idUsuario,
        dt_evento: dtEvento,
        id_rota: null,
        id_linha: editedRoute && editedRoute._linhaExistente != null
            ? editedRoute._linhaExistente
            : null,
        cod_rota: editedRoute && editedRoute.cod_rota ? editedRoute.cod_rota : null,
        nome_rota: editedRoute && editedRoute.nome_rota ? editedRoute.nome_rota : null,
        id_empresa: baseline.id_empresa,
        id_cliente: baseline.id_cliente,
        id_cerca_destino: baseline.id_cerca_destino,
        nome_destino: baseline.nome_destino,
        id_modo_roteirizacao: idModoRoteirizacao != null
            ? Number(idModoRoteirizacao)
            : (baseline.id_modo_roteirizacao != null ? Number(baseline.id_modo_roteirizacao) : null),
        constancia_custo: normalizeConstanciaCusto(
            constanciaCusto != null ? constanciaCusto : baseline.constancia_custo,
        ),
        km: baseline.km,
        duracao_seg: baseline.duracao_seg,
        custo_total: baseline.custo_total,
        custo_fixo: baseline.custo_fixo,
        custo_variavel: baseline.custo_variavel,
        custo_mao_obra: baseline.custo_mao_obra,
        parametros_custo_json: cloneJson(baseline.parametros_custo_json),
        geojson: baseline.geojson,
        shape: baseline.shape,
        diff_json: null,
        id_historico_original: null,
    };
}

export function buildPublicadaPayload({
    idGrupo,
    baseline,
    editedRoute,
    mapRoute,
    idUsuario,
    dtEvento,
    idHistoricoOriginal,
    idVersao,
    idModoRoteirizacao,
    constanciaCusto,
}) {
    const meters = editedRoute && editedRoute.distancia != null
        ? editedRoute.distancia
        : (mapRoute && mapRoute.distancia);
    const km = metersToKm(meters);
    const params = baseline && baseline.parametros_custo_json
        ? baseline.parametros_custo_json
        : {};
    const costs = computeRouteCost(km, params);
    const current = {
        km,
        custo_total: costs.custo_total,
        duracao_seg: Number(
            (editedRoute && editedRoute.duracao != null)
                ? editedRoute.duracao
                : (mapRoute && mapRoute.duracao),
        ) || 0,
    };
    const original = {
        km: baseline ? baseline.km : null,
        custo_total: baseline ? baseline.custo_total : null,
        duracao_seg: baseline ? baseline.duracao_seg : null,
    };
    const geo = (editedRoute && editedRoute.geojson != null)
        ? geojsonToString(editedRoute.geojson)
        : geojsonToString(mapRoute && mapRoute.geojson);
    const shape = (editedRoute && editedRoute.shape != null)
        ? String(editedRoute.shape)
        : (mapRoute && mapRoute.shape != null ? String(mapRoute.shape) : null);

    return {
        id_grupo: idGrupo,
        id_versao: idVersao != null ? Number(idVersao) : 2,
        tipo_evento: TIPO_EVENTO.PUBLICADA,
        origem: ORIGEM.ROTEIRIZACAO,
        id_usuario: idUsuario,
        dt_evento: dtEvento,
        id_rota: editedRoute && editedRoute.id_rota != null ? editedRoute.id_rota : null,
        id_linha: editedRoute && editedRoute._linhaExistente != null
            ? editedRoute._linhaExistente
            : null,
        cod_rota: editedRoute && editedRoute.cod_rota ? editedRoute.cod_rota : null,
        nome_rota: editedRoute && editedRoute.nome_rota ? editedRoute.nome_rota : null,
        id_empresa: baseline ? baseline.id_empresa : null,
        id_cliente: baseline ? baseline.id_cliente : null,
        id_cerca_destino: baseline ? baseline.id_cerca_destino : null,
        nome_destino: baseline ? baseline.nome_destino : null,
        id_modo_roteirizacao: idModoRoteirizacao != null
            ? Number(idModoRoteirizacao)
            : (baseline && baseline.id_modo_roteirizacao != null
                ? Number(baseline.id_modo_roteirizacao)
                : null),
        constancia_custo: normalizeConstanciaCusto(
            constanciaCusto != null
                ? constanciaCusto
                : (baseline && baseline.constancia_custo),
        ),
        km,
        duracao_seg: current.duracao_seg,
        ...costs,
        parametros_custo_json: cloneJson(params),
        geojson: geo,
        shape,
        diff_json: buildDiffJson(original, current),
        id_historico_original: idHistoricoOriginal,
    };
}

export function buildEditadaCrudPayload({
    baseline,
    rota,
    idUsuario,
    dtEvento,
    idVersao,
    idLinha,
}) {
    const meters = rota && rota.distancia != null ? rota.distancia : null;
    const km = metersToKm(meters);
    const params = baseline.parametros_custo_json || {};
    const costs = computeRouteCost(km, params);
    const current = { km, custo_total: costs.custo_total, duracao_seg: null };
    const original = {
        km: baseline.km != null ? Number(baseline.km) : null,
        custo_total: baseline.custo_total != null ? Number(baseline.custo_total) : null,
        duracao_seg: baseline.duracao_seg,
    };

    return {
        id_grupo: baseline.id_grupo,
        id_versao: idVersao,
        tipo_evento: TIPO_EVENTO.EDITADA_CRUD,
        origem: ORIGEM.CRUD_LINHAS,
        id_usuario: idUsuario,
        dt_evento: dtEvento,
        id_rota: rota.id_rota,
        id_linha: idLinha != null ? idLinha : baseline.id_linha,
        cod_rota: rota.cod_rota || baseline.cod_rota,
        nome_rota: rota.nome_rota || baseline.nome_rota,
        id_empresa: baseline.id_empresa,
        id_cliente: baseline.id_cliente,
        id_cerca_destino: baseline.id_cerca_destino,
        nome_destino: baseline.nome_destino,
        id_modo_roteirizacao: baseline.id_modo_roteirizacao != null
            ? Number(baseline.id_modo_roteirizacao)
            : null,
        constancia_custo: normalizeConstanciaCusto(baseline.constancia_custo),
        km,
        duracao_seg: null,
        ...costs,
        parametros_custo_json: cloneJson(params),
        geojson: geojsonToString(rota.geojson),
        shape: rota.shape != null ? String(rota.shape) : null,
        diff_json: buildDiffJson(original, current),
        id_historico_original: baseline.id_historico_alteracao_rota
            || baseline.id_historico_original
            || null,
    };
}

/**
 * Baseline TRACADO_ANTERIOR: a rota editada no CRUD não tinha ORIGINAL/CALCULADA, então o
 * traçado que ela tinha antes da edição (`anterior`) abre o grupo, para a edição ter com o
 * que ser comparada. `params` são as tarifas da linha (computeRouteCost), usadas também
 * para custear as edições seguintes do grupo.
 */
export function buildTracadoAnteriorPayload({
    anterior,
    idUsuario,
    dtEvento,
    idLinha,
    params,
}) {
    const km = metersToKm(anterior.distancia);
    const parametros = params || {};
    return {
        id_grupo: generateIdGrupo(),
        id_versao: 1,
        tipo_evento: TIPO_EVENTO.TRACADO_ANTERIOR,
        origem: ORIGEM.CRUD_LINHAS,
        id_usuario: idUsuario,
        dt_evento: dtEvento,
        id_rota: anterior.id_rota,
        id_linha: idLinha,
        cod_rota: anterior.cod_rota || null,
        nome_rota: anterior.nome_rota || null,
        id_empresa: anterior.id_empresa != null ? anterior.id_empresa : null,
        id_cliente: anterior.id_cliente != null ? anterior.id_cliente : null,
        id_cerca_destino: null,
        nome_destino: null,
        id_modo_roteirizacao: null,
        constancia_custo: CONSTANCIA_CUSTO.VIAGEM,
        km,
        duracao_seg: null,
        ...computeRouteCost(km, parametros),
        parametros_custo_json: cloneJson(parametros),
        geojson: geojsonToString(anterior.geojson),
        shape: anterior.shape != null ? String(anterior.shape) : null,
        diff_json: null,
        id_historico_original: null,
    };
}

/** Geometria da rota antes de uma edição, para o baseline TRACADO_ANTERIOR. */
export function snapshotTracadoRota(rota) {
    if (!rota || rota.id_rota == null) return null;
    if (!rota.geojson && !rota.shape) return null;
    return {
        id_rota: rota.id_rota,
        cod_rota: rota.cod_rota,
        nome_rota: rota.nome_rota,
        id_empresa: rota.id_empresa,
        id_cliente: rota.id_cliente,
        geojson: rota.geojson,
        shape: rota.shape,
        distancia: rota.distancia,
    };
}

export function nowSqlDatetime() {
    return new Date().toISOString().slice(0, 19).replace('T', ' ');
}
